import { useCallback, useEffect, useState } from 'react'
import {
  addProspectTouch,
  deleteProspect,
  getProspect,
  patchProspect,
  previewProspectEmail,
  sendProspectEmail,
  subscribeProspects,
  setProspectCampaignState,
  TOUCH_CHANNEL_LABEL,
  TOUCH_OUTCOME_LABEL,
  type DraftHistoryEntry,
  type FactLine,
  type PersonaView,
  type RemoteProspect,
  type RemoteProspectCampaign,
  type RemoteProspectTouch,
  type SignatureView,
  type TouchChannel,
  type TouchOutcome
} from '../../lib/adminClient'
import { useAuth } from '../../lib/auth'
import { Card, Counter, Linkify, Pill, StyleIssues, copyThenOpen, inputCls, selectCls, shortUrl, useStyleCheck, when } from './ui'

// One prospect: what to do next and why, the messages, the email, the facts
// with their sources, and everything already sent. The page never sends on
// LinkedIn: it copies the message and opens the profile, and asks afterwards
// whether it was sent, so the record is what actually happened.

const NOTE_MAX = 200
const DM_MAX = 600

type Loaded = {
  p: RemoteProspect
  touches: RemoteProspectTouch[]
  facts: FactLine[]
  history: DraftHistoryEntry[]
  emailBlocker: string | null
  emailCaution: string | null
  signatures: SignatureView[]
}

const STATUS_WORDS: Record<string, string> = {
  not_started: 'Not contacted',
  warming: 'Warming up',
  requested: 'Contacted',
  connected: 'Connected',
  replied: 'Replied',
  converted: 'Signed up',
  declined: 'Said no',
  stopped: 'Stopped'
}

export function PersonDetail({
  id,
  campaigns,
  personas,
  onChanged,
  onDeleted
}: {
  id: string
  campaigns: RemoteProspectCampaign[]
  personas: PersonaView[]
  onChanged: (p: RemoteProspect) => void
  onDeleted: () => void
}): JSX.Element {
  const session = useAuth()
  const canWrite = session?.role === 'owner' || session?.role === 'admin'
  const [data, setData] = useState<Loaded | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [emailOpen, setEmailOpen] = useState(false)

  const load = useCallback(async () => {
    const r = await getProspect(id)
    if (!r.ok) return setError(r.error)
    setError(null)
    setData({
      p: r.data.prospect,
      touches: r.data.touches,
      facts: r.data.facts ?? [],
      history: r.data.draftHistory ?? [],
      emailBlocker: r.data.emailBlocker ?? null,
      emailCaution: r.data.emailCaution ?? null,
      signatures: r.data.signatures ?? []
    })
  }, [id])
  useEffect(() => {
    setData(null)
    setNotice(null)
    setEmailOpen(false)
    void load()
  }, [load])

  const changed = (p: RemoteProspect, msg?: string): void => {
    onChanged(p)
    if (msg) setNotice(msg)
    void load()
  }

  if (error) return <Card><div className="text-sm text-red-300">Could not load this prospect: {error}</div></Card>
  if (!data) return <Card><div className="text-sm text-slate-400">Loading…</div></Card>
  const { p } = data
  const memberOf = p.campaigns.filter((c) => c.state === 'subscribed')

  return (
    <div className="space-y-4" data-testid="person-detail">
      <Header p={p} personas={personas} canWrite={canWrite} onChanged={changed} />
      {notice && (
        <div className="glass-card rounded-xl px-4 py-2 text-sm text-emerald-200" data-testid="person-notice">
          {notice}
        </div>
      )}
      <NextStep
        p={p}
        canWrite={canWrite}
        campaignId={memberOf[0]?.campaignId ?? null}
        onChanged={changed}
        onWriteEmail={() => {
          setEmailOpen(true)
          setTimeout(() => document.querySelector('[data-testid="email"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
        }}
      />
      <EmailPanel
        key={p.id}
        p={p}
        open={emailOpen || p.plan.step === 'email' || (p.plan.step === 'follow_up' && p.plan.message === 'email')}
        setOpen={setEmailOpen}
        blocker={data.emailBlocker}
        caution={data.emailCaution}
        signatures={data.signatures}
        campaigns={memberOf.map((c) => ({ id: c.campaignId, name: c.name }))}
        canWrite={canWrite}
        onChanged={changed}
      />
      <Messages p={p} history={data.history} canWrite={canWrite} onChanged={changed} />
      <Facts p={p} facts={data.facts} canWrite={canWrite} onChanged={changed} />
      <History p={p} touches={data.touches} campaigns={memberOf.map((c) => ({ id: c.campaignId, name: c.name }))} canWrite={canWrite} onChanged={changed} />
      <More p={p} campaigns={campaigns} canWrite={canWrite} onChanged={changed} onDeleted={onDeleted} />
    </div>
  )
}

// ── header ────────────────────────────────────────────────────────────────

function Header({ p, personas, canWrite, onChanged }: { p: RemoteProspect; personas: PersonaView[]; canWrite: boolean; onChanged: (p: RemoteProspect, msg?: string) => void }): JSX.Element {
  async function setPersona(personaId: string): Promise<void> {
    const r = await patchProspect(p.id, { personaId: personaId || null })
    if (r.ok) onChanged(r.data.prospect)
  }
  return (
    <div className="glass-card rounded-xl p-5" data-testid="person-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">{p.name}</h2>
          <div className="text-sm text-slate-300 mt-0.5">{[p.role, p.company].filter(Boolean).join(' · ') || 'Role not recorded'}</div>
          <div className="text-xs text-slate-500 mt-0.5">{[p.location, p.country && !(p.location ?? '').includes(p.country) ? p.country : null].filter(Boolean).join(', ')}</div>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          {p.tier && <Pill tone={p.tier === 'A' ? 'accent' : 'muted'}>Tier {p.tier}</Pill>}
          <Pill>{STATUS_WORDS[p.status] ?? p.status}</Pill>
          {p.doNotSend && <Pill tone="bad" testId="person-held">Held back</Pill>}
          {!p.verified && <Pill tone="warn">Identity not confirmed</Pill>}
          {p.checkedAt ? <Pill tone="good">Facts checked</Pill> : <Pill tone="warn">Facts not checked by you yet</Pill>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
        {p.linkedinUrl ? (
          <a href={p.linkedinUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline" data-testid="person-linkedin">
            LinkedIn profile ↗
          </a>
        ) : (
          <span className="text-slate-500">No LinkedIn profile on record</span>
        )}
        <span className="text-slate-600">·</span>
        <label className="text-slate-400 flex items-center gap-1.5">
          Persona
          <select className={selectCls} value={p.personaId ?? ''} disabled={!canWrite} onChange={(e) => void setPersona(e.target.value)} data-testid="person-persona">
            <option value="">None</option>
            {personas.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        {p.segment && !p.personaName && <span className="text-slate-500">Researched as: {p.segment}</span>}
      </div>
    </div>
  )
}

// ── the next step ────────────────────────────────────────────────────────

function NextStep({
  p,
  canWrite,
  campaignId,
  onChanged,
  onWriteEmail
}: {
  p: RemoteProspect
  canWrite: boolean
  campaignId: string | null
  onChanged: (p: RemoteProspect, msg?: string) => void
  onWriteEmail: () => void
}): JSX.Element {
  const plan = p.plan
  const [asked, setAsked] = useState<null | { channel: TouchChannel; body: string; copy: 'copied' | 'copy_failed' }>(null)
  const [formText, setFormText] = useState(p.draftEmail ?? p.draftDm ?? '')
  useEffect(() => setFormText(p.draftEmail ?? p.draftDm ?? ''), [p.id, p.draftEmail, p.draftDm])
  async function viaForm(): Promise<void> {
    const copy = await copyThenOpen(formText, p.contactFormUrl ?? null)
    setAsked({ channel: 'form', body: formText, copy })
  }
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function log(channel: TouchChannel, direction: 'out' | 'in', outcome: TouchOutcome | '', body: string, msg: string): Promise<void> {
    setBusy(true)
    setErr(null)
    const r = await addProspectTouch(p.id, { channel, direction, outcome, body, campaignId })
    setBusy(false)
    if (!r.ok) return setErr(r.error)
    setAsked(null)
    onChanged(r.data.prospect, msg)
  }
  async function linkedIn(channel: 'note' | 'dm', text: string | null): Promise<void> {
    const copy = await copyThenOpen(text ?? '', p.linkedinUrl)
    setAsked({ channel, body: text ?? '', copy })
  }
  // A free LinkedIn account gets only a few notes a month; a plain request is not limited the same way.
  function connectPlain(): void {
    if (p.linkedinUrl) window.open(p.linkedinUrl, '_blank', 'noopener')
    setAsked({ channel: 'note', body: '', copy: 'copied' })
  }
  async function confirmIdentity(): Promise<void> {
    const r = await patchProspect(p.id, { verified: true })
    if (r.ok) onChanged(r.data.prospect, 'Marked as confirmed: this is them.')
    else setErr(r.error)
  }

  const tone = plan.step === 'hold' ? 'warn' : plan.actionable ? 'accent' : undefined
  return (
    <Card title="Next step" tone={tone} testId="next-step">
      <div className="text-lg font-semibold" data-testid="next-step-title">{plan.title}</div>
      <div className="text-sm text-slate-300 mt-1 max-w-2xl">{plan.why}</div>

      {canWrite && !asked && (
        <div className="flex flex-wrap gap-2 mt-4">
          {plan.step === 'email' && (
            <button className="btn-primary" onClick={onWriteEmail} data-testid="act-email">
              Write the email
            </button>
          )}
          {plan.step === 'form' && (
            <div className="w-full space-y-2">
              <textarea className={inputCls} rows={6} value={formText} onChange={(e) => setFormText(e.target.value)} data-testid="form-text" />
              <div className="text-[11px] text-slate-500">
                The form is at{' '}
                <a className="text-accent hover:underline" href={p.contactFormUrl ?? '#'} target="_blank" rel="noreferrer">
                  {shortUrl(p.contactFormUrl ?? '')}
                </a>
                . Use connect@plexiidesk.com as your address there, so the reply comes back to the team inbox.
              </div>
              <button className="btn-primary" disabled={!formText.trim()} onClick={() => void viaForm()} data-testid="act-form">
                Copy message and open their form
              </button>
            </div>
          )}
          {plan.step === 'connect' && (
            <>
              <button className="btn-primary" onClick={() => void linkedIn('note', p.draftNote)} disabled={!p.draftNote} data-testid="act-connect">
                Copy note and open LinkedIn
              </button>
              <button className="btn-ghost" onClick={connectPlain} data-testid="act-connect-plain" title="For when your free LinkedIn notes for the month are used up">
                Connect without a note
              </button>
            </>
          )}
          {(plan.step === 'message' || (plan.step === 'follow_up' && plan.message !== 'email')) && (
            <button className="btn-primary" onClick={() => void linkedIn('dm', plan.step === 'message' ? p.draftDm : '')} data-testid="act-message">
              {plan.step === 'message' ? 'Copy message and open LinkedIn' : 'Open LinkedIn to follow up'}
            </button>
          )}
          {plan.step === 'verify' && (
            <>
              {p.linkedinUrl && (
                <a className="btn-ghost" href={p.linkedinUrl} target="_blank" rel="noreferrer">
                  Open their profile
                </a>
              )}
              <button className="btn-primary" onClick={() => void confirmIdentity()} data-testid="act-verify">
                Yes, it is them
              </button>
            </>
          )}
          {(plan.step === 'start_there' || plan.step === 'no_route') && (
            <>
              <button className="btn-primary" disabled={busy} onClick={() => void log('newsletter', 'out', 'sent', 'Got in touch where they are active.', 'Logged. Connect on LinkedIn in about a week.')} data-testid="act-did-there">
                I got in touch there
              </button>
              {p.linkedinUrl && p.draftNote && (
                <button className="btn-ghost" onClick={() => void linkedIn('note', p.draftNote)} data-testid="act-connect-anyway">
                  Connect on LinkedIn now instead
                </button>
              )}
            </>
          )}
          {plan.step === 'reply' && (
            <>
              {p.linkedinUrl && (
                <a className="btn-primary" href={p.linkedinUrl} target="_blank" rel="noreferrer">
                  Open LinkedIn
                </a>
              )}
              <button className="btn-ghost" disabled={busy} onClick={() => void log('dm', 'out', 'sent', 'Replied to them.', 'Logged your reply.')} data-testid="act-replied">
                I replied
              </button>
            </>
          )}
          {plan.step === 'wait' && (
            <>
              {p.status === 'requested' && (
                <button className="btn-ghost" disabled={busy} onClick={() => void log('note', 'out', 'accepted', 'They accepted the request.', 'Marked as connected. Next: the first message.')} data-testid="act-accepted">
                  They accepted
                </button>
              )}
              <button className="btn-ghost" disabled={busy} onClick={() => void log('dm', 'in', 'replied', 'They replied.', 'Marked as replied. Your turn.')} data-testid="act-they-replied">
                They replied
              </button>
            </>
          )}
        </div>
      )}

      {asked && (
        <div className="mt-4 rounded-lg bg-white/[0.03] p-3" data-testid="act-confirm">
          <div className="text-sm text-slate-200">
            {asked.copy === 'copied'
              ? asked.channel === 'form'
                ? 'The message is on your clipboard and their contact form is open in a new tab. Paste it, add your name and connect@plexiidesk.com, and send.'
                : asked.channel === 'note' && !asked.body
                ? 'Their profile is open in a new tab. Press Connect, then “Send without a note”. Your first message goes once they accept.'
                : asked.channel === 'note'
                ? 'The note is on your clipboard and their profile is open in a new tab. Press Connect, then “Add a note”, and paste.'
                : 'The message is on your clipboard and their profile is open. Press Message and paste.'
              : 'Your browser would not let the page copy. Select the text below and copy it yourself.'}
          </div>
          {asked.copy === 'copy_failed' && <textarea readOnly value={asked.body} className={`${inputCls} mt-2`} rows={3} onFocus={(e) => e.currentTarget.select()} />}
          <div className="text-xs text-slate-400 mt-2">LinkedIn has no link that opens the connect box with a note already in it, so this is the shortest honest route.</div>
          <div className="flex gap-2 mt-3">
            <button className="btn-primary" disabled={busy} onClick={() => void log(asked.channel, 'out', 'sent', asked.body || '(Request sent without a note.)', asked.channel === 'note' ? 'Logged: request sent. Waiting for them to accept.' : asked.channel === 'form' ? 'Logged: sent through their contact form.' : 'Logged: message sent.')} data-testid="act-sent">
              I sent it
            </button>
            <button className="btn-ghost" onClick={() => setAsked(null)} data-testid="act-not-sent">
              I did not send it
            </button>
          </div>
        </div>
      )}

      {plan.channels.length > 0 && (plan.step === 'start_there' || plan.step === 'no_route' || plan.step === 'connect') && (
        <div className="mt-4" data-testid="next-step-channels">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Where they are active</div>
          <ul className="space-y-1.5">
            {plan.channels.map((c, i) => (
              <li key={i} className="text-sm">
                {c.url ? (
                  <a href={c.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                    {c.label} ↗
                  </a>
                ) : (
                  <span className="text-slate-200">{c.label}</span>
                )}
                <span className="text-slate-400"> · {c.how}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {err && <div className="text-xs text-red-300 mt-2">{err}</div>}
    </Card>
  )
}

// ── messages ─────────────────────────────────────────────────────────────

function Messages({ p, history, canWrite, onChanged }: { p: RemoteProspect; history: DraftHistoryEntry[]; canWrite: boolean; onChanged: (p: RemoteProspect, msg?: string) => void }): JSX.Element {
  const [note, setNote] = useState(p.draftNote ?? '')
  const [dm, setDm] = useState(p.draftDm ?? '')
  const [err, setErr] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const noteIssues = useStyleCheck(note, 'note', p.name)
  const dmIssues = useStyleCheck(dm, 'dm', p.name)
  useEffect(() => {
    setNote(p.draftNote ?? '')
    setDm(p.draftDm ?? '')
  }, [p.id, p.draftNote, p.draftDm])
  const dirty = note !== (p.draftNote ?? '') || dm !== (p.draftDm ?? '')

  async function save(): Promise<void> {
    setErr(null)
    const r = await patchProspect(p.id, { draftNote: note, draftDm: dm })
    if (!r.ok) return setErr(r.error)
    onChanged(r.data.prospect, 'Messages saved. The earlier version is kept below.')
  }
  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      setErr('Your browser would not let the page copy; select the text and copy it yourself.')
    }
  }

  return (
    <Card title="The messages" testId="messages" aside={<span className="text-[11px] text-slate-500">Written for {p.name.split(' ')[0]} alone. Edit freely.</span>}>
      {p.watchOuts && (
        <div className="text-xs text-amber-200 bg-amber-500/5 rounded-md px-3 py-2 mb-3" data-testid="messages-watchouts">
          Careful: <Linkify text={p.watchOuts} />
        </div>
      )}
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-300">LinkedIn connection note</span>
            <span className="flex items-center gap-3">
              <Counter n={note.length} max={NOTE_MAX} />
              <button className="text-xs text-accent hover:underline" onClick={() => void copy(note)}>Copy</button>
            </span>
          </div>
          <textarea className={inputCls} rows={3} value={note} readOnly={!canWrite} onChange={(e) => setNote(e.target.value)} data-testid="draft-note" />
          <StyleIssues issues={noteIssues} testId="draft-note-issues" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-300">First message, once they accept</span>
            <span className="flex items-center gap-3">
              <Counter n={dm.length} max={DM_MAX} />
              <button className="text-xs text-accent hover:underline" onClick={() => void copy(dm)}>Copy</button>
            </span>
          </div>
          <textarea className={inputCls} rows={5} value={dm} readOnly={!canWrite} onChange={(e) => setDm(e.target.value)} data-testid="draft-dm" />
          <StyleIssues issues={dmIssues} testId="draft-dm-issues" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        {canWrite && (
          <button className="btn-primary" disabled={!dirty} onClick={() => void save()} data-testid="draft-save">
            Save messages
          </button>
        )}
        {history.length > 0 && (
          <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => setShowHistory(!showHistory)} data-testid="draft-history-toggle">
            {showHistory ? 'Hide' : 'Show'} earlier versions ({history.length})
          </button>
        )}
        {err && <span className="text-xs text-red-300">{err}</span>}
      </div>
      {showHistory && (
        <ul className="mt-3 space-y-3" data-testid="draft-history">
          {history.map((h) => (
            <li key={h.id} className="text-xs border-l border-white/10 pl-3">
              <div className="text-slate-500">
                Replaced {when(h.replacedAt)}
                {h.replacedBy ? ` by ${h.replacedBy}` : ''}
                {h.reason ? ` · ${h.reason}` : ''}
              </div>
              {h.draftNote && <div className="text-slate-300 mt-1">Note: {h.draftNote}</div>}
              {h.draftDm && <div className="text-slate-300 mt-1">Message: {h.draftDm}</div>}
              {h.draftEmail && (
                <div className="text-slate-300 mt-1 whitespace-pre-wrap">
                  Email{h.draftEmailSubject ? ` (“${h.draftEmailSubject}”)` : ''}: {h.draftEmail}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

// ── email ────────────────────────────────────────────────────────────────

function EmailPanel({
  p,
  open,
  setOpen,
  blocker,
  caution,
  signatures,
  campaigns,
  canWrite,
  onChanged
}: {
  p: RemoteProspect
  open: boolean
  setOpen: (v: boolean) => void
  blocker: string | null
  caution: string | null
  signatures: SignatureView[]
  campaigns: Array<{ id: string; name: string }>
  canWrite: boolean
  onChanged: (p: RemoteProspect, msg?: string) => void
}): JSX.Element {
  const [address, setAddress] = useState(p.email ?? '')
  const [source, setSource] = useState(p.emailSource ?? '')
  const [subject, setSubject] = useState(p.draftEmailSubject ?? '')
  const [body, setBody] = useState(p.draftEmail ?? `Hi ${p.name.replace(/\([^)]*\)/g, '').trim().split(/\s+/)[0]},\n\n`)
  const [sig, setSig] = useState<string>(signatures.find((s) => s.isDefault)?.id ?? '')
  const [campaignId, setCampaignId] = useState<string>(campaigns[0]?.id ?? '')
  const [preview, setPreview] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const issues = useStyleCheck(body, 'email', p.name)

  async function saveAddress(): Promise<void> {
    setErr(null)
    const r = await patchProspect(p.id, { email: address.trim() || null, emailSource: source.trim() || null })
    if (!r.ok) return setErr(r.error)
    onChanged(r.data.prospect, 'Address saved.')
  }
  const draftChanged = subject !== (p.draftEmailSubject ?? '') || (p.draftEmail !== null && body !== p.draftEmail)
  async function saveDraft(): Promise<void> {
    setErr(null)
    const r = await patchProspect(p.id, { draftEmailSubject: subject, draftEmail: body })
    if (!r.ok) return setErr(r.error)
    onChanged(r.data.prospect, 'Email draft saved. The earlier version is kept.')
  }
  async function showPreview(): Promise<void> {
    const r = await previewProspectEmail(p.id, { subject, body, signatureId: sig || null })
    if (!r.ok) return setErr(r.error)
    setPreview(r.data.html)
    setErr(r.data.error)
  }
  async function send(): Promise<void> {
    setBusy(true)
    setErr(null)
    const r = await sendProspectEmail(p.id, { subject, body, signatureId: sig || null, campaignId: campaignId || null })
    setBusy(false)
    setConfirming(false)
    if (!r.ok) return setErr(r.error)
    setPreview(null)
    onChanged(r.data.prospect, r.data.delivered ? `Email sent to ${p.email}. It is in the Sent log and in the history below.` : `Recorded, but the server has no email provider set, so nothing left the building.`)
  }

  return (
    <Card
      title="Email"
      testId="email"
      aside={
        <button className="text-xs text-accent hover:underline" onClick={() => setOpen(!open)} data-testid="email-toggle">
          {open ? 'Hide' : p.email ? 'Write an email' : 'Add an address'}
        </button>
      }
    >
      {!open && <div className="text-sm text-slate-400">{p.email ? `${p.email}${p.emailSource ? ` (from ${p.emailSource})` : ' · source not recorded'}` : 'No address on record. Most of these people are best reached on LinkedIn or where they are active.'}</div>}
      {open && (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <input className={inputCls} placeholder="name@their-site.com" value={address} readOnly={!canWrite} onChange={(e) => setAddress(e.target.value)} data-testid="email-address" />
            <input className={inputCls} placeholder="Where you found it, e.g. the contact page on their site" value={source} readOnly={!canWrite} onChange={(e) => setSource(e.target.value)} data-testid="email-source" />
          </div>
          {canWrite && (address !== (p.email ?? '') || source !== (p.emailSource ?? '')) && (
            <button className="btn-ghost text-xs" onClick={() => void saveAddress()} data-testid="email-save-address">
              Save address
            </button>
          )}
          {p.email && p.emailSource && <div className="text-xs text-slate-400" data-testid="email-source-shown">Source: <Linkify text={p.emailSource} /></div>}
          {caution && !blocker && (
            <div className="text-xs text-amber-200 bg-amber-500/5 rounded-md px-3 py-2" data-testid="email-caution">
              {caution}
            </div>
          )}
          {blocker ? (
            <div className="text-sm text-amber-200" data-testid="email-blocker">{blocker}</div>
          ) : (
            <>
              <input className={inputCls} placeholder="Subject: a few plain words" value={subject} readOnly={!canWrite} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" />
              <textarea className={inputCls} rows={7} value={body} readOnly={!canWrite} onChange={(e) => setBody(e.target.value)} data-testid="email-body" />
              <StyleIssues issues={issues} testId="email-issues" />
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                <label className="flex items-center gap-1.5">
                  Signature
                  <select className={selectCls} value={sig} onChange={(e) => setSig(e.target.value)} data-testid="email-signature">
                    <option value="">None</option>
                    {signatures.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {s.isDefault ? ' (default)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                {campaigns.length > 0 && (
                  <label className="flex items-center gap-1.5">
                    Part of
                    <select className={selectCls} value={campaignId} onChange={(e) => setCampaignId(e.target.value)} data-testid="email-campaign">
                      <option value="">No campaign</option>
                      {campaigns.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <span>Every email adds an unsubscribe line, where you found their address, and our postal address.</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn-ghost" onClick={() => void showPreview()} data-testid="email-preview">
                  Preview
                </button>
                {canWrite && draftChanged && (
                  <button className="btn-ghost" onClick={() => void saveDraft()} data-testid="email-save-draft">
                    Save as draft
                  </button>
                )}
                {canWrite &&
                  (confirming ? (
                    <>
                      <span className="text-sm text-slate-300 self-center">Send to {p.email} now?</span>
                      <button className="btn-primary" disabled={busy} onClick={() => void send()} data-testid="email-confirm">
                        {busy ? 'Sending…' : 'Yes, send'}
                      </button>
                      <button className="btn-ghost" onClick={() => setConfirming(false)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button className="btn-primary" disabled={!subject.trim() || !body.trim()} onClick={() => setConfirming(true)} data-testid="email-send">
                      Send…
                    </button>
                  ))}
              </div>
              {preview && <iframe title="Email preview" sandbox="" srcDoc={preview} className="w-full h-[360px] bg-white rounded" data-testid="email-preview-frame" />}
            </>
          )}
          {err && <div className="text-xs text-red-300" data-testid="email-error">{err}</div>}
        </div>
      )}
    </Card>
  )
}

// ── facts ────────────────────────────────────────────────────────────────

function Facts({ p, facts, canWrite, onChanged }: { p: RemoteProspect; facts: FactLine[]; canWrite: boolean; onChanged: (p: RemoteProspect, msg?: string) => void }): JSX.Element {
  async function mark(checked: boolean): Promise<void> {
    const r = await patchProspect(p.id, { factsChecked: checked })
    if (r.ok) onChanged(r.data.prospect, checked ? 'Marked: you checked the facts against their sources.' : 'Check cleared.')
  }
  return (
    <Card
      title="Check the facts"
      testId="facts"
      aside={
        p.checkedAt ? (
          <span className="text-xs text-emerald-300" data-testid="facts-checked">
            Checked by {p.checkedBy ?? 'an admin'} {when(p.checkedAt)}
            {canWrite && (
              <button className="ml-2 text-slate-500 hover:text-slate-300" onClick={() => void mark(false)}>
                undo
              </button>
            )}
          </span>
        ) : canWrite ? (
          <button className="btn-ghost text-xs" onClick={() => void mark(true)} data-testid="facts-mark">
            I opened the sources and these hold
          </button>
        ) : null
      }
    >
      <div className="text-xs text-slate-400 mb-3">Open each source before you send. The messages may only say what these pages show.</div>
      <dl className="space-y-3">
        {facts.map((f) => (
          <div key={f.key} className={`rounded-lg px-3 py-2 ${f.tone === 'warn' ? 'bg-amber-500/5' : 'bg-white/[0.02]'}`} data-testid={`fact-${f.key}`}>
            <dt className={`text-[11px] uppercase tracking-wider ${f.tone === 'warn' ? 'text-amber-200' : 'text-slate-500'}`}>{f.label}</dt>
            <dd className="text-sm text-slate-200 mt-0.5">
              <Linkify text={f.text} />
            </dd>
            {f.note && f.note !== f.text && (
              <dd className="text-xs text-slate-400 mt-1">
                <Linkify text={f.note} />
              </dd>
            )}
            {f.sources.length > 0 && (
              <dd className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">
                {f.sources.map((u) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline" data-testid="fact-source">
                    {shortUrl(u)} ↗
                  </a>
                ))}
              </dd>
            )}
          </div>
        ))}
      </dl>
    </Card>
  )
}

// ── history ──────────────────────────────────────────────────────────────

function History({
  p,
  touches,
  campaigns,
  canWrite,
  onChanged
}: {
  p: RemoteProspect
  touches: RemoteProspectTouch[]
  campaigns: Array<{ id: string; name: string }>
  canWrite: boolean
  onChanged: (p: RemoteProspect, msg?: string) => void
}): JSX.Element {
  const [adding, setAdding] = useState(false)
  const [channel, setChannel] = useState<TouchChannel>('comment')
  const [direction, setDirection] = useState<'out' | 'in'>('out')
  const [outcome, setOutcome] = useState<TouchOutcome | ''>('sent')
  const [body, setBody] = useState('')
  const [campaignId, setCampaignId] = useState(campaigns[0]?.id ?? '')
  const [err, setErr] = useState<string | null>(null)
  async function add(): Promise<void> {
    const r = await addProspectTouch(p.id, { channel, direction, outcome, body, campaignId: campaignId || null })
    if (!r.ok) return setErr(r.error)
    setAdding(false)
    setBody('')
    onChanged(r.data.prospect, 'Logged.')
  }
  return (
    <Card
      title={`What has happened (${touches.length})`}
      testId="history"
      aside={
        canWrite && !adding ? (
          <button className="text-xs text-accent hover:underline" onClick={() => setAdding(true)} data-testid="history-add">
            Log something else
          </button>
        ) : null
      }
    >
      {adding && (
        <div className="rounded-lg bg-white/[0.03] p-3 mb-3 space-y-2" data-testid="history-form">
          <div className="flex flex-wrap gap-2">
            <select className={selectCls} value={channel} onChange={(e) => setChannel(e.target.value as TouchChannel)}>
              {(Object.keys(TOUCH_CHANNEL_LABEL) as TouchChannel[]).map((c) => (
                <option key={c} value={c}>
                  {TOUCH_CHANNEL_LABEL[c]}
                </option>
              ))}
            </select>
            <select className={selectCls} value={direction} onChange={(e) => setDirection(e.target.value as 'out' | 'in')}>
              <option value="out">We sent it</option>
              <option value="in">They sent it</option>
            </select>
            <select className={selectCls} value={outcome} onChange={(e) => setOutcome(e.target.value as TouchOutcome | '')}>
              <option value="">Outcome not known</option>
              {(Object.keys(TOUCH_OUTCOME_LABEL) as TouchOutcome[]).map((o) => (
                <option key={o} value={o}>
                  {TOUCH_OUTCOME_LABEL[o]}
                </option>
              ))}
            </select>
            {campaigns.length > 0 && (
              <select className={selectCls} value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
                <option value="">No campaign</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <textarea className={inputCls} rows={2} placeholder="What was said (optional)" value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="flex gap-2">
            <button className="btn-primary" onClick={() => void add()}>Log it</button>
            <button className="btn-ghost" onClick={() => setAdding(false)}>Cancel</button>
            {err && <span className="text-xs text-red-300 self-center">{err}</span>}
          </div>
        </div>
      )}
      {touches.length === 0 ? (
        <div className="text-sm text-slate-500">Nothing sent or received yet.</div>
      ) : (
        <ol className="space-y-2.5">
          {touches.map((t) => (
            <li key={t.id} className="text-sm border-l border-white/10 pl-3" data-testid={`touch-${t.id}`}>
              <div className="text-slate-200">
                {t.direction === 'in' ? 'They' : 'We'}: {TOUCH_CHANNEL_LABEL[t.channel] ?? t.channel}
                {t.outcome ? ` · ${TOUCH_OUTCOME_LABEL[t.outcome]}` : ''}
                {t.campaignName ? <span className="text-slate-500"> · {t.campaignName}</span> : null}
              </div>
              <div className="text-[11px] text-slate-500">
                {when(t.occurredAt)}
                {t.adminEmail ? ` · ${t.adminEmail}` : ''}
                {t.emailSendId ? (
                  <>
                    {' · '}
                    <a className="text-accent hover:underline" href={`/emails?tab=log&q=${encodeURIComponent(p.email ?? '')}`}>
                      in the Sent log
                    </a>
                  </>
                ) : null}
              </div>
              {t.body && <div className="text-xs text-slate-400 mt-0.5 whitespace-pre-wrap">{t.body}</div>}
            </li>
          ))}
        </ol>
      )}
    </Card>
  )
}

// ── everything else ──────────────────────────────────────────────────────

function More({
  p,
  campaigns,
  canWrite,
  onChanged,
  onDeleted
}: {
  p: RemoteProspect
  campaigns: RemoteProspectCampaign[]
  canWrite: boolean
  onChanged: (p: RemoteProspect, msg?: string) => void
  onDeleted: () => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState(p.notes ?? '')
  const [form, setForm] = useState(p.contactFormUrl ?? '')
  const [holdReason, setHoldReason] = useState('')
  const [addTo, setAddTo] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => setNotes(p.notes ?? ''), [p.id, p.notes])
  useEffect(() => setForm(p.contactFormUrl ?? ''), [p.id, p.contactFormUrl])
  const memberIds = new Set(p.campaigns.filter((c) => c.state === 'subscribed').map((c) => c.campaignId))

  async function patch(body: Record<string, unknown>, msg: string): Promise<void> {
    setErr(null)
    const r = await patchProspect(p.id, body)
    if (!r.ok) return setErr(r.error)
    onChanged(r.data.prospect, msg)
  }
  async function addCampaign(): Promise<void> {
    if (!addTo) return
    const r = await subscribeProspects(addTo, [p.id])
    if (!r.ok) return setErr(r.error)
    if (r.data.refused.length) setErr(r.data.refused[0].reason)
    setAddTo('')
    const fresh = await getProspect(p.id)
    if (fresh.ok) onChanged(fresh.data.prospect, 'Added to the campaign.')
  }
  async function leave(campaignId: string): Promise<void> {
    const r = await setProspectCampaignState(campaignId, p.id, 'unsubscribed')
    if (!r.ok) return setErr(r.error)
    const fresh = await getProspect(p.id)
    if (fresh.ok) onChanged(fresh.data.prospect, 'Taken out of the campaign.')
  }
  async function remove(): Promise<void> {
    const r = await deleteProspect(p.id)
    if (!r.ok) return setErr(r.error)
    onDeleted()
  }

  return (
    <Card title="More" testId="more" aside={<button className="text-xs text-accent hover:underline" onClick={() => setOpen(!open)} data-testid="more-toggle">{open ? 'Hide' : 'Show'}</button>}>
      {!open ? (
        <div className="text-sm text-slate-400">
          Campaigns ({memberIds.size}), notes, contact form, holding back, ad lists, delete.
        </div>
      ) : (
        <div className="space-y-5 text-sm">
          <div>
            <div className="text-xs text-slate-400 mb-1.5">Campaigns</div>
            {p.campaigns.filter((c) => c.state === 'subscribed').map((c) => (
              <div key={c.campaignId} className="flex items-center gap-2">
                <span>{c.name}</span>
                {canWrite && (
                  <button className="text-xs text-slate-500 hover:text-slate-300" onClick={() => void leave(c.campaignId)}>
                    take out
                  </button>
                )}
              </div>
            ))}
            {canWrite && (
              <div className="flex gap-2 mt-1.5">
                <select className={selectCls} value={addTo} onChange={(e) => setAddTo(e.target.value)} data-testid="more-campaign">
                  <option value="">Add to a campaign…</option>
                  {campaigns.filter((c) => !memberIds.has(c.id)).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button className="btn-ghost text-xs" disabled={!addTo} onClick={() => void addCampaign()}>
                  Add
                </button>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1.5">Your notes</div>
            <textarea className={inputCls} rows={3} value={notes} readOnly={!canWrite} onChange={(e) => setNotes(e.target.value)} data-testid="more-notes" />
            {canWrite && notes !== (p.notes ?? '') && (
              <button className="btn-ghost text-xs mt-1.5" onClick={() => void patch({ notes }, 'Notes saved.')}>
                Save notes
              </button>
            )}
          </div>
          <div>
            <div className="text-xs text-slate-400 mb-1.5">Contact form on their own site (used when there is no address)</div>
            <input className={inputCls} placeholder="https://their-site.com/contact" value={form} readOnly={!canWrite} onChange={(e) => setForm(e.target.value)} data-testid="more-form-url" />
            {canWrite && form.trim() !== (p.contactFormUrl ?? '') && (
              <button className="btn-ghost text-xs mt-1.5" onClick={() => void patch({ contactFormUrl: form.trim() || null }, form.trim() ? 'Contact form saved.' : 'Contact form cleared.')} data-testid="more-form-save">
                {form.trim() ? 'Save contact form' : 'Clear contact form'}
              </button>
            )}
          </div>
          {canWrite && (
            <div>
              <div className="text-xs text-slate-400 mb-1.5">{p.doNotSend ? `Held back: ${p.doNotSendReason ?? ''}` : 'Hold this one back'}</div>
              <div className="flex gap-2">
                <input className={inputCls} placeholder="Why, in a sentence (needed either way)" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} data-testid="more-hold-reason" />
                <button className="btn-ghost text-xs whitespace-nowrap" disabled={!holdReason.trim()} onClick={() => void patch({ doNotSend: !p.doNotSend, doNotSendReason: holdReason.trim() }, p.doNotSend ? 'No longer held back.' : 'Held back.')} data-testid="more-hold">
                  {p.doNotSend ? 'Release' : 'Hold back'}
                </button>
              </div>
            </div>
          )}
          <div>
            <div className="text-xs text-slate-400 mb-1">Ad lists</div>
            <div className="text-xs text-slate-500">
              {p.email ? 'This person can go into the hashed match lists on the Ad lists tab.' : 'No address, so this person cannot be matched on any ad platform. No platform gives a per-person id.'}
            </div>
          </div>
          <div className="text-xs text-slate-500">
            From {p.source ?? 'an unknown source'}
            {p.batch ? `, batch “${p.batch}”` : ''}. Added {when(p.createdAt)}.
          </div>
          {canWrite &&
            (confirmDelete ? (
              <div className="flex gap-2 items-center">
                <span className="text-xs text-red-300">Delete {p.name} and everything logged about them?</span>
                <button className="btn-ghost text-xs text-red-300" onClick={() => void remove()} data-testid="more-delete-confirm">
                  Yes, delete
                </button>
                <button className="btn-ghost text-xs" onClick={() => setConfirmDelete(false)}>
                  Cancel
                </button>
              </div>
            ) : (
              <button className="text-xs text-slate-500 hover:text-red-300" onClick={() => setConfirmDelete(true)} data-testid="more-delete">
                Delete this prospect
              </button>
            ))}
          {err && <div className="text-xs text-red-300">{err}</div>}
        </div>
      )}
    </Card>
  )
}
